import type {
  DayGlance, StreakState, ThoughtRecord, VoiceLog } from '@adhd/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrainIcon } from '../components/Icons.js';
import { CheckIn } from './CheckIn.js';
import { Conditions } from '../components/Conditions.js';
import { RateNow } from '../components/RateNow.js';
import { TenDays } from '../components/TenDays.js';
import { FaceMorning } from '../components/FaceMorning.js';
import { FaceNight } from '../components/FaceNight.js';
import { Door } from '../components/Door.js';
import { faceByClock, overrideFace, readOverride, writeOverride, type Face, type FaceOverride } from '../lib/face.js';
import { DaySummary } from '../components/DaySummary.js';
import { TodayDoses } from '../components/TodayDoses.js';
import { covering } from '../components/DayLifeEvent.js';
import { KeepInMindRow } from '../components/KeepInMind.js';
import { api, type DailyQuote, type DayFace, type OwnLifeEvent, type TodayMood } from '../lib/api.js';
import type { Wave } from '@adhd/shared';
import { useSession } from '../lib/session.js';
import { useNow, useOnResume } from '../lib/fresh.js';
import { foldLabel } from '../lib/format.js';
import { DayFeed, type FeedItem } from '../components/DayFeed.js';
import { MarkChips } from '../components/MarkChips.js';

/**
 * Today.
 *
 * Priorities, top to bottom: SOS first (findable without reading anything),
 * then the two lowest-friction logging actions, then what you've already done
 * today, then something kind to read. Nothing here nags, and nothing shows a
 * number you can fail at.
 */
export function Home(): JSX.Element {
  const [today, setToday] = useState<TodayMood | null>(null);
  // Bumped to make the day fold refetch after an edit.
  const [glanceKey, setGlanceKey] = useState(0);
  /* Still fetched: onCheckinRemoved refreshes it, and Trends reads it. The
     card that drew it on this screen is gone. */
  const [, setStreak] = useState<StreakState | null>(null);
  // Which number the card shows, and whether it is being held for today — one
  // rule, shared with Trends, so the two screens cannot disagree about it.
  const [quote, setQuote] = useState<DailyQuote | null>(null);
  const [events, setEvents] = useState<OwnLifeEvent[]>([]);
  // Release 2 of 2.0: how many people the record names, for the chip. Owner-only.
  const [peopleCount, setPeopleCount] = useState(0);
  /*
   * THE DAY'S FACES, release 3 of 2.0. The clock decides in the owner's zone;
   * a tap overrides it for the session. `dayFace` is the day's own answers —
   * last night, and whether it is closed — fetched for the day the night
   * belongs to, which before 4am is yesterday.
   */
  const { user } = useSession();
  const [stored, setStored] = useState<FaceOverride | null>(null);
  const [dayFace, setDayFace] = useState<DayFace | null>(null);
  const [nightQuote, setNightQuote] = useState<DailyQuote | null>(null);
  // Release 5 of 2.0: who tapped "thinking of you" today. A line, never a count.
  const [waves, setWaves] = useState<Wave[]>([]);
  const [glances, setGlances] = useState<DayGlance[] | null>(null);
  /*
   * Which day the fold below is showing.
   *
   * Null means "whatever makes sense right now", which is today unless today is
   * both brand new and empty — at 00:15 the most useful thing on the screen is
   * the day just finished, and an empty block where a read should be reads as
   * broken rather than as early.
   */
  const [picked, setPicked] = useState<string | null>(null);
  /*
   * The rest of the day, which this screen has never shown.
   *
   * Fetched as a small page and filtered on `day` rather than asked for with
   * `?from=`: that parameter filters `recorded_at`, the instant, while every
   * other surface groups by the frozen `day` column. The two disagree exactly
   * when he has travelled — which is a live case on this screen, since it also
   * renders the travel prompt.
   */
  const [entries, setEntries] = useState<VoiceLog[]>([]);
  const [records, setRecords] = useState<ThoughtRecord[]>([]);
  // Six, then the rest on request. Cutting is what keeps Today from becoming
  // the Log; the control is there so a heavy day is never actually hidden.
  const [showAll, setShowAll] = useState(false);
  /* The full check-in form, with the note. Closed until the slider's "add a
     note" asks for it: the slider IS the check-in now (release 1 of 2.0). */
  const [formOpen, setFormOpen] = useState(false);
  /*
   * THE CLOCK THIS SCREEN READS (20 Oct 2026). It read `new Date()` during
   * whatever render last happened, and an installed app resumed from the
   * background does not render, so at 3pm it could still say "morning" and
   * show the morning face. `useNow` moves on the minute and on coming back to
   * the app (lib/fresh.tsx), and everything below that depends on the time —
   * the face, the date line, which day it is before 4am — reads it.
   */
  const now = useNow();
  const todayKey = now.toLocaleDateString('en-CA');
  const nightDay = now.getHours() < 4 ? yesterdayOf(todayKey) : todayKey;
  /*
   * THE DAY THIS SCREEN IS, 14 Oct 2026. Before 4am Today is still yesterday's
   * night — the app's own boundary, which the fold, the faces and the close
   * already kept while the slider, the marks, the threads line and the date
   * line quietly used the calendar date. One day for all of it: everything
   * here files under and reads from `dayKey`. A journal said at 00:53 went to
   * the new date because of the split.
   */
  const dayKey = nightDay;
  const loadEvents = useCallback(() => {
    void api.lifeEvents().then(setEvents).catch(() => {});
  }, []);

  /*
   * Reloaded together, because removing a check-in moves more than one thing:
   * the list, the streak, and the figures inside "How today's going". Leaving
   * any of them stale would have the screen disagreeing with itself about a day
   * he had just edited, which is the failure this app keeps finding.
   */
  /*
   * `refreshToday`, not `onCheckinRemoved`: it runs on SAVE too.
   *
   * It was written for the remove path and then handed to the inline check-in
   * as `onSaved` in release 5 without being renamed, which is part of how the
   * strip came to be missed — the one reader who would have asked "does this
   * refresh everything a save changes?" was looking at a function whose name
   * said it only ran on deletions.
   */
  /*
   * Keyed on the day (20 Oct 2026). Both of these were written with `[]` and
   * read `dayKey` from the first render, so a Today left open across 4am went
   * on fetching and saving under the day before for as long as it stayed
   * mounted.
   */
  const refreshToday = useCallback(() => {
    void api.todayMood(dayKey).then(setToday).catch(() => {});
    void api.streak().then(setStreak).catch(() => {});
    setGlanceKey((n) => n + 1);
  }, [dayKey]);

  const loadDay = (): void => {
    void api.todayMood(dayKey).then(setToday).catch(() => {});
    void api.streak().then(setStreak).catch(() => {});
    void api.quote().then(setQuote).catch(() => {});
    void api.listJournal(20).then((r) => setEntries(r.items)).catch(() => {});
    void api
      .listThoughtRecords('?limit=20')
      .then((r) => setRecords(r.items))
      .catch(() => {});
    loadEvents();
    void api.people().then((p) => setPeopleCount(p.length)).catch(() => {});
    void api.waves().then(setWaves).catch(() => {});
  };
  useEffect(() => {
    loadDay();
    // A new day starts on itself: the strip's pick and the night's quote
    // belonged to the day that has just ended.
    setPicked(null);
    setNightQuote(null);
  }, [dayKey]);
  useEffect(() => {
    setStored(readOverride(nightDay));
    void api.dayFace(nightDay).then(setDayFace).catch(() => {});
  }, [nightDay]);
  /*
   * BACK IN THE APP after a minute or more away (20 Oct 2026): everything on
   * this screen is fetched again where it stands. A check-in made on the
   * laptop, the night's sleep the Shortcut sent at 7am, a wave — none of them
   * reached a Today that was already open. In place, never a remount: a
   * half-said entry in the door is not the screen's to throw away.
   */
  useOnResume(() => {
    loadDay();
    void api.dayFace(nightDay).then(setDayFace).catch(() => {});
    setGlanceKey((n) => n + 1);
  });
  /* Yesterday's rows are not today's, for the beat between the day turning
     and the new day's fetch landing. */
  const todayRows = today?.day === dayKey ? today : null;

  const checkinsToday = todayRows?.items.length ?? 0;
  /*
   * Today unless today has nothing in it yet and the day is barely started.
   *
   * The fold is the one thing on this screen that reads a whole day, and a day
   * fifteen minutes old has nothing to read — the app deliberately declines to
   * write about it until it is four hours in. Showing an empty block there is
   * technically honest and practically useless, so before that point it falls
   * back to yesterday and says which day it is showing.
   */
  const byClock = faceByClock(now, user?.morningEndsAt ?? '11:00', user?.nightStartsAt ?? '21:00');
  // A tap's choice holds only while the clock still shows the face it was
  // made from (15 Oct 2026); then the clock picks again.
  const override = overrideFace(stored, byClock);
  // A closed day shows as closed whatever the hour, until it is reopened.
  const face: Face = dayFace?.closedAt ? 'night' : (override ?? byClock);
  const pickFace = (f: Face): void => {
    setStored({ face: f, from: byClock });
    writeOverride(nightDay, f, byClock);
  };
  useEffect(() => {
    if (face === 'night' && !nightQuote) void api.quote('night').then(setNightQuote).catch(() => {});
  }, [face, nightQuote]);
  const dayName = new Date(`${nightDay}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' });
  const todayGlance = glances?.find((g) => g.day === dayKey) ?? null;
  // The fold follows the same day as the rest of the screen (it used to fall
  // back to yesterday only while today was empty, which is the same rule said
  // twice: before 4am, today IS yesterday here).
  const focusDay = picked ?? dayKey;

  /*
   * The day, newest first, from three lists that arrive separately.
   *
   * Filtered on `day`, the frozen key every other surface groups by, so an
   * entry cannot appear here under today and in the Log under yesterday.
   */
  const feed: FeedItem[] = [
    ...(todayRows?.items ?? []).map((c) => ({ kind: 'checkin' as const, at: c.recordedAt, data: c })),
    ...entries
      .filter((e) => e.day === dayKey)
      .map((e) => ({ kind: 'journal' as const, at: e.recordedAt, data: e })),
    ...records
      .filter((r) => r.day === dayKey)
      .map((r) => ({ kind: 'record' as const, at: r.occurredAt, data: r })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  // Records drafted from an entry, so each is drawn under its parent and not
  // also on its own. Same map the Log builds.
  const derivedRecords = new Map(
    records.filter((r) => r.voiceLogId).map((r) => [r.voiceLogId!, r]),
  );

  return (
    <div className="stack-lg stack">
      {/*
        THE CONDITIONS BLOCK, release 1 of 2.0 (docs/PRD-2.0.md §2).

        The latest check-in's band tints the top of the screen and its word is
        the largest thing on it; the greeting and the describing sentence move
        inside it for a day with no check-in yet. Settings keeps its place, top
        right. What is running — life events, with his own day count — sits
        under the word as the threads strip.
      */}
      <Conditions
        suffix={
          dayKey !== todayKey
            ? `${new Date(`${dayKey}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' })}’s night`
            : face === 'morning' ? 'morning' : face === 'night' ? 'evening' : now.getHours() < 12 ? 'morning' : now.getHours() < 18 ? 'afternoon' : 'evening'
        }
        last={todayRows?.items[0] ?? null}
        checkins={checkinsToday}
        describe={describeDay(checkinsToday, todayGlance, dayKey !== todayKey ? `on ${dayName}` : 'today')}
        events={runningOn(events, dayKey)}
        today={dayKey}
        peopleCount={peopleCount}
        onThreadStarted={loadEvents}
      />

      {/* Asked once, and only about the schedule.
          Which day a thing belongs to already follows the device with nothing
          asked, because that is a fact about where he was rather than a choice.
          What time an evening dose is due is the choice — 8pm in New York is 2pm
          in Honolulu — and it is a medical one the app has no business settling
          on its own. Below SOS, because nothing outranks that. */}
      <TravelPrompt />

      {/*
        THE BIG SOS BUTTON WAS HERE, and it has gone — not the route, the
        button. "Help now" is the fourth nav tab (App.tsx), fixed to the bottom
        of every screen including this one, so it was already present twice on
        the only screen that had it and absent from none. What leaves is a
        second copy; what stays is the one that is always there.
      */}


      {/*
        THE CHECK-IN IS TODAY.
        
        It was a row here reading "How are you doing? · 15 sec" that travelled
        to /check-in, so the thing this app exists for sat one navigation away
        from the screen it opens on. At 11:40pm on a bad day that navigation is
        where people stop. Inline it is two gestures — drag the thumb, tap Save
        — and no route change happens before a check-in is saved.

        /check-in stays as a deep link; the same component renders both.
      */}
      {/*
        `last` is the day's most recent check-in, so the row can say what was
        said instead of asking again. /mood/today returns newest-first.

        `today` is null until its effect lands, so for one paint this is `null`
        — the row reads "How are you doing?" on a day that already has a
        check-in on it. That is the same brief state the header above it
        already shows (describeDay with checkinsToday 0 reads "Nothing logged
        yet today"), and the row is the same height either way, so nothing
        jumps. Fixing it properly means holding the whole block back until the
        fetch lands, which trades a wrong word for an empty screen.
      */}
      {/*
        THE SLIDER IS THE CHECK-IN, always, in the middle of the screen —
        release 1 of 2.0. Dragging it writes a check-in on release and nothing
        else. The form below it, with the note and the four ratings, opens from
        the slider's "add a note" and arrives open; it is the same component
        that renders /check-in, so the row the 2 October change built is still
        what the deep link and check:checkin-row see.
      */}
      <RateNow
        last={todayRows?.items[0] ?? null}
        day={dayKey}
        onSaved={refreshToday}
        onMore={() => setFormOpen(true)}
      />
      {formOpen && (
        <CheckIn inline startOpen day={dayKey} count={checkinsToday} onSaved={() => { refreshToday(); setFormOpen(false); }} last={todayRows?.items[0] ?? null} />
      )}

      {/*
        THE DOSES SIT UNDER THE CHECK-IN, and that is a reversal.

        The plan put a due dose first, on the reasoning that a dose has a time
        it was meant to happen where a check-in is whenever. The plan ALSO set
        a hard test: the question, the slider, the word, the note and Save are
        all visible at 375x812 without scrolling, or it fails. Those two cannot
        both hold while the dose block is a card.

        Measured, with two unanswered doses: Save ran to y783 against a tab bar
        at y748 — thirty-five pixels of the button hidden behind the nav, on
        the ordinary morning state of anybody who takes medication. Which is to
        say: on Nick's own screen, every morning, while the earlier
        verification was done on an account with no medications at all and
        reported 144px of headroom.

        The test wins. A dose that has to be scrolled to is one tap late; a
        Save button behind the tab bar is a check-in that does not happen.
      */}
      {/* Who is thinking of you today. One line per person; the most recent
          tap per link; nothing counted. Release 5 of 2.0. */}
      {waves.filter((w) => w.at.slice(0, 10) >= todayKey).length > 0 && (
        <ul className="waves" aria-label="Thinking of you">
          {[...new Map(waves.filter((w) => w.at.slice(0, 10) >= todayKey).map((w) => [w.shareId, w])).values()].map((w) => (
            <li key={w.id} className="wave-line">
              <span className="wave-av" aria-hidden="true">{w.label.slice(0, 2)}</span>
              <span><b>{w.label}</b> is thinking of you · {new Date(w.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Which face. The clock picks; a tap overrides for the session. A
          closed day is the night face until it is reopened. */}
      {/* No switch (the fixes pass, 13 Oct 2026: "i dont understand the morning
          now night"). The clock picks the face; the only seams are one link at
          the foot of each face — Start the day, Close the day, Back to the day.
          `pickFace` still holds the session override those links set. */}

      {face === 'morning' && (
        <FaceMorning
          day={nightDay}
          sleep={dayFace?.sleep ?? null}
          sleepSource={dayFace?.sleepSource ?? null}
          onSleep={(face) => setDayFace(face)}
          quote={quote}
          onStart={() => pickFace('now')}
        />
      )}
      {face === 'morning' && (
        <Door
          day={dayKey}
          prompt="Anything on your mind before the day?"
          onSaved={() => void api.listJournal(20).then((r) => setEntries(r.items)).catch(() => {})}
          onFiled={refreshToday}
        />
      )}

      {face === 'night' && (
        <FaceNight
          day={nightDay}
          dayName={dayName}
          face={dayFace}
          quote={nightQuote}
          onBack={() => pickFace('now')}
          onFace={(f) => {
            setDayFace(f);
            setGlanceKey((n) => n + 1);
          }}
          onSaved={() => {
            void api.listJournal(20).then((r) => setEntries(r.items)).catch(() => {});
            setGlanceKey((n) => n + 1);
          }}
        />
      )}

      {face === 'now' && (
      <>
      {/* THE DOOR — release 4 of 2.0. One input; the entry is saved first,
          then the sorter's picks come back as receipts. The slider above it
          is the check-in that needs none of this. */}
      <Door
        day={dayKey}
        onSaved={() => void api.listJournal(20).then((r) => setEntries(r.items)).catch(() => {})}
        onFiled={refreshToday}
      />
      <TodayDoses />

      {/* "Log something" and its two cards went with the door (PRD-2.0 §2; the
          fixes, 14 Oct): the door is how you talk about the day, and the
          slider is the check-in. One quiet way in to a worksheet stays, and
          a running "keep in mind" shows only while there is one. */}
      <div className="stack-sm stack">
        <KeepInMindRow />
        <Link className="linkish" to="/thought-record">
          <BrainIcon /> Work through a thought ›
        </Link>
      </div>

      {/* Between the doses and "Log something": after the thing he came to do,
          before the list of things he might do. */}
      <MarkChips day={dayKey} />
      {/* The one seam on Now: the night face, early. */}
      <div className="seam">
        <button type="button" className="linkish" onClick={() => pickFace('night')}>
          Close the day ›
        </button>
        <span className="faint">the night face: the door, the night dose, close</span>
      </div>
      </>
      )}

      {face !== 'morning' && (
      <>

      {/*
        THE WHOLE DAY, NOT JUST THE RATINGS.
        
        This section listed check-ins and nothing else, so a day spent writing
        showed as a column of bare mood cards with the writing nowhere on the
        screen — while the Log showed the writing and folded the check-ins away.
        The same day, two screens, each hiding what the other led with.
        
        Gated on the items themselves rather than on `checkinsToday`, which is
        what used to make a journal-only day render nothing at all directly
        under a sentence saying he had written something today.
      */}
      {feed.length > 0 && (
        <section className="stack">
          {/* Before 4am the list is the night's day, and says so, as the
              date line and the counts already do (22 Oct 2026). */}
          <h2>{dayKey !== todayKey ? `${dayName} so far` : 'Today so far'}</h2>
          <DayFeed
            items={showAll ? feed : feed.slice(0, 6)}
            today={todayKey}
            onCheckinRemoved={refreshToday}
            records={derivedRecords}
            quiet
          />
          {feed.length > 6 && !showAll && (
            <button className="linkish linkish-quiet" onClick={() => setShowAll(true)}>
              {feed.length - 6} more from earlier {dayKey !== todayKey ? `on ${dayName}` : 'today'}
            </button>
          )}
        </section>
      )}

      {/*
        THE STREAK CARD WAS HERE.

        "13 days in a row · best run 17" is a score, and a perfect run is a
        loaded gun pointed at the next day — DESIGN.md says so in writing. The
        card knew it: it carried two kind sentences about gaps, "a gap isn't a
        reset" and "yesterday was a day off, log anything today and the run
        carries on", which are answers to a problem the card itself creates.
        Delete the card and the problem goes with it.

        `totalActiveDays` and the streak itself still exist and still show on
        Trends, where looking at them is a thing you choose to do rather than
        something the app does to you while you are trying to log a bad day.
      */}

      {/* Today, added up. Below the doses, the check-in and the streak on
          purpose: this screen's job is what to do next, and a read of how the
          day is going is something to find rather than be met by — particularly
          on the days it says something hard. Folded for the same reason. */}
      <section className="stack">
        {/* The week first, then today in detail. A strip is a glance and costs
            nothing to draw — no model call, only figures and any sentence that
            already existed — so it can sit open. The detailed read stays folded
            because it is the one that says something about you. */}
        {/* `refreshKey` is why the strip is no longer the last thing on this
            screen still describing the day before the save.

            `glanceKey` already existed and already bumped on every save and
            every removal — it keyed the detailed fold below. It was never
            handed to the strip, whose own effect depended on nothing but a
            setState, so the strip fetched once on mount and then stayed where
            it was. The header said "checked in 2 times today · Awful" and the
            strip, three centimetres under it, said "+2 / Alright" about the
            same day. */}
        <RecapStrip
          days={glances}
          onLoaded={setGlances}
          refreshKey={glanceKey}
          picked={focusDay}
          onPick={setPicked}
          today={todayKey}
        />
        {/* No rail here. The comment below is explicit that this area takes no
            border, no card and no colour; the mark obeys it and is text only. */}
        {/* Running events only, and Today's existing rule about that is kept: an
            event that has ENDED still frames a past day in the Log, but on the
            screen he opens each morning a finished thing has finished. */}
        <details className="day-summary-fold" key={focusDay}>
          {/* The only trace any of this leaves on Today, and it is a concession
              rather than a feature: it does put the fact of the hard thing on
              the screen he opens every morning. But it is a muted clause on a
              line that was already here, inside a control he can leave shut —
              not a card, not a border, not a colour, not a button. Without it a
              marked event goes on silently explaining flat Tuesdays for months.

              The unnamed form still carries the fact and the date, which is the
              whole guard against an event nobody remembers marking. */}
          <summary>{foldLabel(focusDay, todayKey)}</summary>
          {/* `glanceKey` remounts this after a check-in is removed, so the
              figures inside the fold cannot go on counting one that is
              gone. */}
          <TodaySummary
            key={`${focusDay}:${glanceKey}`}
            day={focusDay}
          />
        </details>
      </section>

      {face === 'now' && quote && (
        <section className="card">
          <p className="reading">{quote.text}</p>
          <p className="faint" style={{ marginTop: 'var(--space-3)' }}>
            — {quote.author}
            {quote.sourceNote && (
              <span style={{ display: 'block', marginTop: 4 }}>{quote.sourceNote}</span>
            )}
          </p>
        </section>
      )}
      </>
      )}
    </div>
  );
}


/**
 * "You've been on a different clock for a while."
 *
 * Raised only after the difference has persisted — see noteTravel on the server
 * — so a layover or an hour in another zone never produces a question about
 * medication timing. Either answer silences it for this trip; coming home
 * forgets the trip entirely, so the next one is asked about fresh.
 */
function TravelPrompt(): JSX.Element | null {
  // Every hook before the early return. Calling useSession() again down in the
  // JSX — which is what this did — is a second hook behind a conditional
  // return, and React counts hooks per render.
  const { travel, setTravel, refresh, user } = useSession();
  const [busy, setBusy] = useState(false);
  if (!travel) return null;

  const home = (user?.timezone.split('/').pop() ?? '').replace(/_/g, ' ');

  const place = travel.zone.split('/').pop()?.replace(/_/g, ' ') ?? travel.zone;
  const answer = (a: 'moved' | 'kept'): void => {
    setBusy(true);
    void api
      .answerTravel(a)
      .then(() => {
        setTravel(null);
        return refresh();
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="banner banner-caution stack-sm stack">
      <strong>You've been on {place} time for a while.</strong>
      <p className="muted" style={{ margin: 0 }}>
        Your days are already being recorded where you are. Your dose times are still set in {home}{' '}
        time — an evening dose is due at the same clock time it was at home.
      </p>
      <div className="row">
        <button className="btn btn-secondary grow" disabled={busy} onClick={() => answer('moved')}>
          Use {place} time
        </button>
        <button className="btn btn-secondary grow" disabled={busy} onClick={() => answer('kept')}>
          Keep them as they are
        </button>
      </div>
    </div>
  );
}

/** Local midday, so a DST hop cannot skip a date. */
function yesterdayOf(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-CA');
}



/** The same rule, as a list, for the block that draws them. */
function runningOn(events: OwnLifeEvent[], day: string): OwnLifeEvent[] {
  return covering(events, day).filter((e) => e.endedOn === null);
}


/**
 * The first line on the screen, and it has to agree with the rest of it.
 *
 * This counted check-ins only, so somebody who opened Help now twice at 1:19am
 * and rated nothing was told "Nothing logged yet today. No pressure." two
 * hundred pixels above a card reading "TODAY · 2 SOS". The reassurance is the
 * right instinct and completely wrong on that morning — it tells a person who
 * had a bad night that the app did not notice.
 *
 * Help now gets its own sentence rather than being folded into a count. Reading
 * "You've logged 2 things today" about two crisis moments would be worse than
 * saying nothing: it files the worst part of somebody's night under admin.
 */
function describeDay(checkins: number, day: DayGlance | null, when = 'today'): string {
  const sos = day?.sosEvents ?? 0;
  /*
   * Help now comes first, whether or not anything else happened.
   *
   * The fix described above was written for the zero-check-in case only — the
   * test was `checkins === 0 && sos > 0` — so somebody who reached the crisis
   * screen nine times AND checked in once was told "You've checked in once
   * today." The one check-in outranked the nine. That is the same failure the
   * comment above says it fixed: an opening line that tells a person having the
   * worst day of their month that the app noticed the smallest part of it.
   *
   * The check-in count is not lost; it is on the strip directly below. What is
   * on this line is whichever fact matters most, and on a day like that it is
   * never the check-in.
   */
  if (sos > 0) {
    return sos === 1
      ? `You reached for help once ${when}. That counts.`
      : `You reached for help ${sos} times ${when}. That counts.`;
  }
  if (checkins === 0) {
    const other = (day?.thoughtRecords ?? 0) + (day?.journalEntries ?? 0);
    if (other > 0) return `You've written something ${when}, but not checked in.`;
    const meds = day?.medsTaken ?? 0;
    if (meds > 0) {
      /*
       * "Ticked off" is a thing the person did, and `medsTaken` includes the
       * doses nobody touched — the app counts a slot as taken unless told
       * otherwise, which is right for adherence and wrong as a description of
       * somebody's morning. On a fresh account whose only "activity" was a
       * dose it had assumed, this was the first sentence on the screen, and it
       * described an action that had not happened.
       */
      return (day?.medsAssumed ?? 0) >= meds
        ? `Nothing logged yet ${when}. Doses are counted unless you say otherwise.`
        : 'Doses are ticked off. Nothing else logged yet.';
    }
    return `Nothing logged yet ${when}. No pressure.`;
  }
  if (checkins === 1) return `You've checked in once ${when}.`;
  return `You've checked in ${checkins} times ${when}.`;
}



/**
 * Today's summary, fetched only once the fold is opened.
 *
 * `<details>` renders its children regardless of state, so without the gate this
 * would total up the day — and pay for a model call — on every visit to the home
 * screen, including the many visits that are really just "did I take my pills".
 */
function TodaySummary({
  day,
}: {
  day: string;
}): JSX.Element {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const parent = document.getElementById('today-summary')?.closest('details');
    if (!parent) return;
    /* Remounted inside a fold that is already open — `glanceKey` does that
       after every save, and now on coming back to the app — it would wait for
       a toggle that is never coming and draw nothing under an open fold. */
    if (parent.open) setOpened(true);
    const onToggle = (): void => {
      if (parent.open) setOpened(true);
    };
    parent.addEventListener('toggle', onToggle);
    return () => parent.removeEventListener('toggle', onToggle);
  }, []);

  return (
    <div id="today-summary">
      <DaySummary day={day} active={opened} />
    </div>
  );
}

/** Ten days, drawn from counted figures. Release 1 of 2.0 replaced the seven-card strip. */
function RecapStrip({
  days,
  onLoaded,
  refreshKey,
  picked,
  onPick,
  today,
}: {
  days: DayGlance[] | null;
  /** Lifted, because Today needs to know whether today is empty yet. */
  onLoaded: (d: DayGlance[]) => void;
  /** Bumped by `refreshToday`; refetches the days. See the call site. */
  refreshKey: number;
  picked: string;
  onPick: (day: string) => void;
  today: string;
}): JSX.Element | null {
  useEffect(() => {
    let cancelled = false;
    api
      .dayGlances(10)
      .then((d) => !cancelled && onLoaded(d))
      .catch(() => !cancelled && onLoaded([]));
    return () => {
      cancelled = true;
    };
  }, [onLoaded, refreshKey]);
  if (!days || days.length === 0) return null;
  return <TenDays days={days} picked={picked} onPick={onPick} today={today} />;
}
